"use client";

import { Logo } from "@/components/rac/logo";
import { SearchModeToggle } from "@/components/search/search-mode-toggle";
import { useSearchMode } from "@/hooks/use-search-mode";
import { getCityNameFromCode } from "@/info/cities";
import { Filters } from "@/types/filters";
import { BreadcrumbItem } from "@/utils/breadcrumb-utils";
import { buildBreadcrumbItems } from "@/utils/breadcrumbs/search";
import { getSearchTitle } from "@/utils/search/get-search-title";
import { ChevronRight } from "lucide-react";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";

interface IOSSafeHeaderProps {
	city: string;
	filters?: Partial<Filters>;
}

export function IOSSafeHeader({ city, filters = {} }: IOSSafeHeaderProps) {
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	const { mode: searchMode } = useSearchMode();
	const cityDisplayName = getCityNameFromCode(city);

	// Generate dynamic title using filters
	const dynamicTitle = useMemo(() => {
		return getSearchTitle({ cityDisplayName, filters });
	}, [cityDisplayName, filters]);

	// Build breadcrumb items
	const breadcrumbItems = useMemo(() => {
		return buildBreadcrumbItems({ city: cityDisplayName, filters });
	}, [cityDisplayName, filters]);

	if (!mounted) {
		return null;
	}

	// Handle breadcrumb clicks
	const handleBreadcrumbClick = (item: BreadcrumbItem) => {
		if (item.href) {
			window.location.href = item.href;
		}
	};

	return (
		<header
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				width: "100%",
				height: "64px",
				backgroundColor: "#ffffff",
				zIndex: 10000,
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				padding: "0 16px",
				boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
				WebkitBackfaceVisibility: "hidden",
				backfaceVisibility: "hidden",
				WebkitTransform: "translateZ(0)",
				transform: "translateZ(0)",
			}}
		>
			{/* Left - Logo */}
			<div style={{ display: "flex", alignItems: "center", height: "100%" }}>
				<Link href="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
					<Logo className="h-6 sm:h-8 w-auto" />
				</Link>
			</div>

			{/* Center - Title and Breadcrumbs */}
			{searchMode === "manual" && (
				<div
					style={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						padding: "0 60px 0 16px",
						minWidth: 0,
						gap: "0px",
					}}
				>
					{/* Title */}
					<h1
						style={{
							fontSize: "16px",
							fontWeight: "bold",
							color: "#000000",
							margin: 0,
							padding: 0,
							textAlign: "center",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
							width: "100%",
							lineHeight: "1.2",
						}}
					>
						{dynamicTitle}
					</h1>

					{/* Breadcrumbs */}
					<nav
						style={{
							display: "flex",
							alignItems: "center",
							gap: "4px",
							fontSize: "12px",
							color: "#64748b",
							marginTop: "2px",
						}}
						aria-label="Breadcrumb"
					>
						<ol
							style={{
								display: "flex",
								alignItems: "center",
								gap: "4px",
								listStyle: "none",
								margin: 0,
								padding: 0,
								flexWrap: "nowrap",
								overflow: "hidden",
							}}
						>
							{breadcrumbItems.map((item, index) => {
								const isLastItem = index === breadcrumbItems.length - 1;
								return (
									<li
										key={index}
										style={{
											display: "flex",
											alignItems: "center",
											gap: "4px",
											flexShrink: index === breadcrumbItems.length - 1 ? 0 : 1,
											minWidth: 0,
										}}
									>
										{index > 0 && (
											<ChevronRight
												style={{
													width: "12px",
													height: "12px",
													color: "#94a3b8",
													flexShrink: 0,
												}}
											/>
										)}
										<button
											onClick={() => !isLastItem && handleBreadcrumbClick(item)}
											disabled={isLastItem}
											style={{
												background: "none",
												border: "none",
												padding: 0,
												color: isLastItem ? "#0f172a" : "#64748b",
												cursor: isLastItem ? "default" : "pointer",
												fontSize: "12px",
												fontWeight: isLastItem ? 600 : 400,
												textDecoration: "none",
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
												WebkitTapHighlightColor: "transparent",
											}}
										>
											{item.label}
										</button>
									</li>
								);
							})}
						</ol>
					</nav>
				</div>
			)}

			{/* Right - SearchModeToggle */}
			<div
				style={{
					position: "absolute",
					right: "16px",
					top: "50%",
					transform: "translateY(-50%)",
					zIndex: 50,
				}}
			>
				<SearchModeToggle />
			</div>
		</header>
	);
}
